import type { IntlShape } from 'react-intl';

import { getDataSourceTitle } from '@atlassian/third-party-connector-configuration/connector-display-utils';
import type { ThirdPartyConnectorType } from '@atlassian/third-party-connector-configuration/connector-display-utils';

import type { ConnectorState, RecommendedConnector } from '../connector-promo-config-types.ts';

/**
 * Team type to ordered recommended connector type keys.
 * Keys match the connector type identifiers used by the
 * third-party-connector-configuration package.
 */
export const TEAM_CONNECTOR_MAP: Record<string, ThirdPartyConnectorType[]> = {
	'SOFTWARE DEVELOPMENT': [
		'platform-google-drive-connector',
		'platform-sharepoint-connector',
		'github-connector',
	],
	DEFAULT: ['platform-google-drive-connector', 'platform-sharepoint-connector', 'slack-connector'],
	'PROJECT MANAGEMENT': [
		'platform-google-drive-connector',
		'platform-sharepoint-connector',
		'slack-connector',
	],
	OPERATIONS: [
		'platform-google-drive-connector',
		'platform-sharepoint-connector',
		'servicenow-connector',
	],
	'PRODUCT MANAGEMENT': [
		'platform-google-drive-connector',
		'platform-sharepoint-connector',
		'slack-connector',
	],
	DESIGN: ['platform-google-drive-connector', 'platform-sharepoint-connector', 'slack-connector'],
	'IT SUPPORT': [
		'platform-google-drive-connector',
		'platform-sharepoint-connector',
		'servicenow-connector',
	],
	MARKETING: [
		'platform-google-drive-connector',
		'platform-sharepoint-connector',
		'hubspot-connector',
	],
	SALES: [
		'platform-google-drive-connector',
		'platform-sharepoint-connector',
		'salesforce-connector',
	],
	FINANCE: ['platform-google-drive-connector', 'platform-sharepoint-connector', 'slack-connector'],
	'HUMAN RESOURCES': [
		'platform-google-drive-connector',
		'platform-sharepoint-connector',
		'workday-v2-connector',
	],
	LEGAL: ['platform-google-drive-connector', 'platform-sharepoint-connector', 'slack-connector'],
	'DATA SCIENCE': [
		'platform-google-drive-connector',
		'platform-sharepoint-connector',
		'databricks-connector',
	],
	'CUSTOMER SERVICE': [
		'platform-google-drive-connector',
		'platform-sharepoint-connector',
		'zendesk-connector',
	],
};

/**
 * Maps a team type and the current connector states to an ordered list of
 * recommended connectors that the user has NOT yet set up.
 *
 * Connectors with an enabled data source (hasEnabledDataSource === true) are
 * filtered out. They should not appear in the sidebar, empty state, or Nth
 * promo surfaces. The first set-up connector's display name is returned as
 * connectedAppName for post-connect promo copy.
 *
 * Display names are resolved from the third-party-connector-configuration
 * package config. Connector state is derived from admin_getAllDataSources.
 *
 * @param teamType - The uppercased team type (e.g., 'SOFTWARE_DEVELOPMENT')
 * @param connectorStates - A record mapping connector type keys to their state
 * @returns Unconnected recommendations and the first connected app's display name
 */
export function getRecommendedConnectors(
	teamType: string | undefined,
	connectorStates: Record<string, ConnectorState>,
	intl: IntlShape,
	debugConnectorKey?: string,
): { recommendedConnectors: RecommendedConnector[]; connectedAppName: string | undefined } {
	const connectorKeys =
		teamType && TEAM_CONNECTOR_MAP[teamType]
			? TEAM_CONNECTOR_MAP[teamType]
			: TEAM_CONNECTOR_MAP.DEFAULT;

	const recommendedConnectors: RecommendedConnector[] = [];
	let connectedAppName: string | undefined;

	for (const connectorKey of connectorKeys) {
		const state = connectorStates[connectorKey];
		const isSetUp = state?.hasEnabledDataSource === true;
		const title = getDataSourceTitle(connectorKey, undefined);
		const displayName =
			typeof title === 'string' ? title || connectorKey : intl.formatMessage(title);

		if (isSetUp) {
			if (connectedAppName === undefined) {
				connectedAppName = displayName;
			}
		} else {
			recommendedConnectors.push({ key: connectorKey, displayName });
		}
	}

	if (debugConnectorKey) {
		const debugTitle = getDataSourceTitle(debugConnectorKey, undefined);
		const debugDisplayName =
			typeof debugTitle === 'string'
				? debugTitle || debugConnectorKey
				: intl.formatMessage(debugTitle);
		recommendedConnectors.unshift({ key: debugConnectorKey, displayName: debugDisplayName });
	}

	return { recommendedConnectors, connectedAppName };
}
